import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { ModalsProvider } from '@mantine/modals'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/dates/styles.css'
import App from './App.tsx'
import './styles/index.css'
import axios from 'axios';
import { AuthProvider } from './hooks/auth';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

// Handle CORS errors and other API errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response?.data?.error === 'Invalid origin') {
      // CORS validation failed
      const errorMessage = error.response.data.message || 'CORS validation failed';
      console.error('CORS Error:', errorMessage);
      
      // Show user-friendly error message
      const corsError = new Error(
        `Access denied: Your client URL is not allowed by the server. ` +
        `Please check that your client is running on the correct URL and that the server's CORS_ORIGIN configuration matches.`
      );
      corsError.name = 'CORS_ERROR';
      throw corsError;
    }
    
    if (error.response?.status === 401) {
      // Session expired or invalid token
      console.log('Session expired, redirecting to login page');
      
      // Clear authentication data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      
      // Redirect to login page
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Re-throw other errors
    throw error;
  }
);

// Add request interceptor to ensure token is always set
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const token = localStorage.getItem('authToken');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider
        theme={{
          fontFamily: 'Inter, system-ui, sans-serif',
          primaryColor: 'blue',
          colors: {
            blue: [
              '#f0f9ff',
              '#e0f2fe',
              '#bae6fd',
              '#7dd3fc',
              '#38bdf8',
              '#0ea5e9',
              '#0284c7',
              '#0369a1',
              '#075985',
              '#0c4a6e',
            ],
          },
        }}
      >
        <ModalsProvider>
          <Notifications />
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </ModalsProvider>
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>,
) 